// Admin CRUD: product families (list, add, edit, delete)

import { useState, useMemo } from 'react';
import { useProductFamilies, useProductStatuses, useCategories, useProductFamilyMixOptions, useOrigins, useSuppliers, useBrands, useFamilyItemAttributes } from '@/hooks/use-store';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { ProductFamily } from '@/lib/store-types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ArrowLeft } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { postWorkerApi, type WorkerApiError } from '@/lib/worker-api';
import { ImageUploadField } from '@/components/store/ImageUploadField';
import { SecondaryImagesList } from '@/components/store/SecondaryImagesList';
import { StoreThumbnailImg } from '@/components/store/StoreThumbnailImg';
import { Textarea } from '@/components/ui/textarea';
import { MultiSelect } from '@/components/ui/multi-select';
import { Sparkles, Loader2, Search } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

export default function AdminProductFamilies() {
  const { data: families, isLoading } = useProductFamilies(null);
  const { data: statuses } = useProductStatuses();
  const { data: categories } = useCategories();
  const { data: origins } = useOrigins();
  const { data: suppliers } = useSuppliers();
  const { data: brands } = useBrands();
  const { data: familyAttributes = [] } = useFamilyItemAttributes();
  const queryClient = useQueryClient();
  /** Filters: origin/supplier/brand are derived from products (first/any item) per family. */
  const [searchName, setSearchName] = useState('');
  const [filterOriginId, setFilterOriginId] = useState('');
  const [filterSupplierId, setFilterSupplierId] = useState('');
  const [filterBrandId, setFilterBrandId] = useState('');
  /** Filtro por origen en el multiselect "Familias con las que se puede mezclar" (solo en add/edit). */
  const [mixOriginFilterId, setMixOriginFilterId] = useState('');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<ProductFamily | null>(null);
  const [family_name, setFamily_name] = useState('');
  const [slug, setSlug] = useState('');
  const [showroom_image, setShowroom_image] = useState('');
  const [secondary_images, setSecondary_images] = useState<string[]>([]);
  const [marketing_description, setMarketing_description] = useState('');
  const [status_id, setStatus_id] = useState('');
  /** Category IDs this family belongs to (for edit: move family between categories). */
  const [categoryIds, setCategoryIds] = useState<string[]>([]);
  /** Family IDs that can be mixed with this family in a mix container. */
  const [mixOptionFamilyIds, setMixOptionFamilyIds] = useState<string[]>([]);
  const { toast } = useToast();
  useProductFamilyMixOptions(); // keep cache for invalidation

  /** Map family_id → sets of origin_id, supplier_id, brand_id from its products (view admin_family_item_attributes). */
  const familyAttributesMap = useMemo(() => {
    const m = new Map<string, { originIds: Set<string>; supplierIds: Set<string>; brandIds: Set<string> }>();
    for (const row of familyAttributes) {
      let entry = m.get(row.family_id);
      if (!entry) {
        entry = { originIds: new Set(), supplierIds: new Set(), brandIds: new Set() };
        m.set(row.family_id, entry);
      }
      if (row.origin_id) entry.originIds.add(row.origin_id);
      if (row.supplier_id) entry.supplierIds.add(row.supplier_id);
      if (row.brand_id) entry.brandIds.add(row.brand_id);
    }
    return m;
  }, [familyAttributes]);

  const filteredFamilies = useMemo(() => {
    const list = families ?? [];
    const nameLower = searchName.trim().toLowerCase();
    return list.filter((f) => {
      if (nameLower && !f.family_name.toLowerCase().includes(nameLower)) return false;
      const attrs = familyAttributesMap.get(f.id);
      if (filterOriginId && (!attrs || !attrs.originIds.has(filterOriginId))) return false;
      if (filterSupplierId && (!attrs || !attrs.supplierIds.has(filterSupplierId))) return false;
      if (filterBrandId && (!attrs || !attrs.brandIds.has(filterBrandId))) return false;
      return true;
    });
  }, [families, searchName, filterOriginId, filterSupplierId, filterBrandId, familyAttributesMap]);

  /** Familias disponibles para "mezclar con": excluye la actual al editar; opcionalmente filtra por origen. */
  const mixOptionCandidates = useMemo(() => {
    const list = (families ?? []).filter((f) => !editing || f.id !== editing.id);
    if (!mixOriginFilterId) return list;
    return list.filter((f) => familyAttributesMap.get(f.id)?.originIds.has(mixOriginFilterId));
  }, [families, editing, mixOriginFilterId, familyAttributesMap]);

  // AI catalog image: prompt preview and generate
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiLoadingPrompt, setAiLoadingPrompt] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [batchGenerating, setBatchGenerating] = useState(false);
  /** Family ids for which "Generar imagen" (row) was clicked; button shows spinner and is disabled until request completes. */
  const [generatingOneIds, setGeneratingOneIds] = useState<Set<string>>(new Set());

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (categoryIds.length === 0) throw new Error('Selecciona al menos una categoría.');
      if (editing) {
        const { error } = await supabase.from('product_families').update({
          family_name, slug, showroom_image: showroom_image || null,
          secondary_images: secondary_images.filter(Boolean).length ? secondary_images.filter(Boolean) : null,
          marketing_description: marketing_description || null, status_id: status_id || undefined,
        }).eq('id', editing.id);
        if (error) throw error;
        await supabase.from('product_family_categories').delete().eq('family_id', editing.id);
        for (const catId of categoryIds) {
          const { error: jErr } = await supabase.from('product_family_categories').insert({ family_id: editing.id, category_id: catId });
          if (jErr) throw jErr;
        }
        await supabase.from('product_family_mix_options').delete().eq('family_id', editing.id);
        for (const relatedId of mixOptionFamilyIds) {
          if (relatedId === editing.id) continue;
          const { error: mErr } = await supabase.from('product_family_mix_options').insert({ family_id: editing.id, related_family_id: relatedId });
          if (mErr) throw mErr;
        }
      } else {
        const { data: inserted, error } = await supabase.from('product_families').insert({
          family_name, slug, showroom_image: showroom_image || null,
          secondary_images: secondary_images.filter(Boolean).length ? secondary_images.filter(Boolean) : null,
          marketing_description: marketing_description || null, status_id: status_id || (statuses?.[0]?.id),
        }).select('id').single();
        if (error) throw error;
        for (const catId of categoryIds) {
          const { error: jErr } = await supabase.from('product_family_categories').insert({ family_id: inserted.id, category_id: catId });
          if (jErr) throw jErr;
        }
        for (const relatedId of mixOptionFamilyIds) {
          if (relatedId === inserted.id) continue;
          const { error: mErr } = await supabase.from('product_family_mix_options').insert({ family_id: inserted.id, related_family_id: relatedId });
          if (mErr) throw mErr;
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store', 'product_families'] });
      queryClient.invalidateQueries({ queryKey: ['store', 'product_family_categories'] });
      queryClient.invalidateQueries({ queryKey: ['store', 'product_family_mix_options'] });
      queryClient.invalidateQueries({ queryKey: ['store', 'item_family_category'] });
      setOpen(false);
      setEditing(null);
      resetForm();
      toast({ title: editing ? 'Familia actualizada' : 'Familia creada' });
    },
    onError: (e) => toast({ title: 'Error', description: e instanceof Error ? e.message : 'Failed', variant: 'destructive' }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('product_families').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store', 'product_families'] });
      toast({ title: 'Family deleted' });
    },
    onError: (e) => toast({ title: 'Error', description: e instanceof Error ? e.message : 'Failed', variant: 'destructive' }),
  });

  function resetForm() {
    setFamily_name('');
    setSlug('');
    setShowroom_image('');
    setSecondary_images([]);
    setMarketing_description('');
    setStatus_id(statuses?.[0]?.id ?? '');
    setCategoryIds([]);
    setMixOptionFamilyIds([]);
    setEditing(null);
  }

  async function openEdit(f: ProductFamily) {
    setEditing(f);
    setFamily_name(f.family_name);
    setSlug(f.slug);
    setShowroom_image(f.showroom_image ?? '');
    setSecondary_images(f.secondary_images ?? []);
    setMarketing_description(f.marketing_description ?? '');
    setStatus_id(f.status_id);
    const { data: junction } = await supabase.from('product_family_categories').select('category_id').eq('family_id', f.id);
    setCategoryIds((junction ?? []).map((r) => r.category_id));
    const { data: mixRows } = await supabase.from('product_family_mix_options').select('family_id, related_family_id');
    const relatedIds = new Set<string>();
    (mixRows ?? []).forEach((r: { family_id: string; related_family_id: string }) => {
      if (r.family_id === f.id) relatedIds.add(r.related_family_id);
      if (r.related_family_id === f.id) relatedIds.add(r.family_id);
    });
    setMixOptionFamilyIds(Array.from(relatedIds));
    setMixOriginFilterId('');
    setOpen(true);
  }

  async function openAiGenerate() {
    if (!editing?.id) return;
    setAiLoadingPrompt(true);
    setAiDialogOpen(true);
    setAiPrompt('');
    try {
      const result = await postWorkerApi<{ prompt?: string }>('/api/generate-catalog-image', { type: 'family', id: editing.id });
      if (!result.ok) {
        const err = result as WorkerApiError;
        throw new Error(err.error || `HTTP ${err.status}`);
      }
      setAiPrompt(result.data.prompt ?? '');
    } catch (e) {
      toast({ title: 'Error', description: e instanceof Error ? e.message : 'Error al cargar el prompt', variant: 'destructive' });
    } finally {
      setAiLoadingPrompt(false);
    }
  }

  async function confirmAiGenerate() {
    if (!editing?.id) return;
    setAiGenerating(true);
    try {
      const result = await postWorkerApi<{ imageUrl?: string }>('/api/generate-catalog-image', {
        type: 'family',
        id: editing.id,
        confirm: true,
        promptOverride: aiPrompt || undefined,
      });
      if (!result.ok) {
        const err = result as WorkerApiError;
        throw new Error(err.error || `HTTP ${err.status}`);
      }
      setShowroom_image(result.data.imageUrl ?? '');
      queryClient.invalidateQueries({ queryKey: ['store', 'product_families'] });
      setAiDialogOpen(false);
      toast({ title: 'Imagen generada', description: 'Portada actualizada correctamente.' });
    } catch (e) {
      toast({ title: 'Error', description: e instanceof Error ? e.message : 'Error al generar', variant: 'destructive' });
    } finally {
      setAiGenerating(false);
    }
  }

  /** Generate family image in background (202); user can close dialog and keep working. */
  async function confirmAiGenerateBackground() {
    if (!editing?.id) return;
    setAiGenerating(true);
    try {
      const result = await postWorkerApi<{ imageUrl?: string; message?: string }>('/api/generate-catalog-image', {
        type: 'family',
        id: editing.id,
        confirm: true,
        promptOverride: aiPrompt || undefined,
        background: true,
      });
      if (!result.ok) {
        const err = result as WorkerApiError;
        throw new Error(err.error || `HTTP ${err.status}`);
      }
      queryClient.invalidateQueries({ queryKey: ['store', 'product_families'] });
      if (result.status === 202) {
        setAiDialogOpen(false);
        toast({
          title: 'Generación en segundo plano',
          description: result.data.message ?? 'La imagen se actualizará en unos segundos. Puedes cerrar y seguir trabajando.',
        });
      } else {
        setShowroom_image(result.data.imageUrl ?? '');
        setAiDialogOpen(false);
        toast({ title: 'Imagen generada', description: 'Portada actualizada correctamente.' });
      }
    } catch (e) {
      toast({ title: 'Error', description: e instanceof Error ? e.message : 'Error al generar', variant: 'destructive' });
    } finally {
      setAiGenerating(false);
    }
  }

  /** Generate cover image for one family in background (from list row). Button shows spinner and is disabled until request completes. */
  async function handleGenerateOneFamily(family: ProductFamily) {
    setGeneratingOneIds((prev) => new Set(prev).add(family.id));
    try {
      const result = await postWorkerApi<{ imageUrl?: string }>('/api/generate-catalog-image', {
        type: 'family',
        id: family.id,
        confirm: true,
        background: true,
      });
      if (!result.ok) {
        const err = result as WorkerApiError;
        throw new Error(err.error || `HTTP ${err.status}`);
      }
      toast({
        title: result.status === 202 ? 'Generando en segundo plano' : 'Imagen generada',
        description: result.status === 202
          ? `Imagen para «${family.family_name}» se actualizará en unos segundos.`
          : 'Portada actualizada.',
      });
      queryClient.invalidateQueries({ queryKey: ['store', 'product_families'] });
      if (result.status === 202) {
        setTimeout(() => queryClient.invalidateQueries({ queryKey: ['store', 'product_families'] }), 15000);
      }
    } catch (e) {
      toast({ title: 'Error', description: e instanceof Error ? e.message : 'Error al generar', variant: 'destructive' });
    } finally {
      setGeneratingOneIds((prev) => {
        const next = new Set(prev);
        next.delete(family.id);
        return next;
      });
    }
  }

  async function handleBatchGenerate() {
    setBatchGenerating(true);
    try {
      const result = await postWorkerApi<{ generated?: number; message?: string; errors?: { id: string; message: string }[] }>(
        '/api/generate-catalog-image-batch',
        { type: 'family' }
      );
      if (!result.ok) {
        const err = result as WorkerApiError;
        throw new Error(err.error || `HTTP ${err.status}`);
      }
      queryClient.invalidateQueries({ queryKey: ['store', 'product_families'] });
      const data = result.data;
      const isBackground = result.status === 202;
      const description = isBackground
        ? (data.message ?? 'Las imágenes se crearán en segundo plano. Puedes cerrar esta pestaña.')
        : `Generadas ${data.generated ?? 0} imagen(es).${data.errors?.length ? ` Errores: ${data.errors.map((e) => e.message).join('; ')}` : ''}`;
      toast({
        title: isBackground ? 'Generación en segundo plano' : 'Generación por lotes',
        description,
      });
    } catch (e) {
      toast({ title: 'Error', description: e instanceof Error ? e.message : 'Error al iniciar generación', variant: 'destructive' });
    } finally {
      setBatchGenerating(false);
    }
  }

  // Full-page form when adding or editing (avoids modal overflow and ensures mix options are visible).
  if (open) {
    return (
      <div className="min-h-0 flex flex-col">
        <header className="flex items-center gap-4 mb-6 shrink-0">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => { setOpen(false); setEditing(null); resetForm(); }}
            className="gap-1.5 -ml-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Volver
          </Button>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">
            {editing ? 'Editar familia' : 'Nueva familia'}
          </h1>
        </header>
        <div className="flex-1 min-h-0 overflow-y-auto pb-10">
          <div className="w-full max-w-4xl space-y-10">
            {/* Datos básicos */}
            <section className="rounded-xl border bg-card p-6 shadow-sm">
              <h2 className="text-sm font-medium text-foreground mb-4">Datos básicos</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label className="text-foreground">Nombre de la familia</Label>
                  <Input value={family_name} onChange={(e) => setFamily_name(e.target.value)} className="h-10" />
                </div>
                <div className="space-y-2">
                  <Label className="text-foreground">Slug</Label>
                  <Input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="ej. whole-milk-powder" className="h-10" />
                </div>
              </div>
              <div className="mt-5 max-w-[200px] space-y-2">
                <Label className="text-foreground">Estado</Label>
                <select className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm" value={status_id} onChange={(e) => setStatus_id(e.target.value)}>
                  {(statuses ?? []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
            </section>

            {/* Categorías */}
            <section className="rounded-xl border bg-card p-6 shadow-sm">
              <h2 className="text-sm font-medium text-foreground mb-1">Categorías</h2>
              <p className="text-xs text-muted-foreground mb-4">Marca las categorías donde debe listarse esta familia.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                {(categories ?? []).map((c) => (
                  <label key={c.id} className="flex items-center gap-3 rounded-lg border border-transparent bg-muted/40 px-3 py-2 cursor-pointer hover:bg-muted/60 transition-colors has-[:checked]:border-primary/30 has-[:checked]:bg-primary/5">
                    <input
                      type="checkbox"
                      checked={categoryIds.includes(c.id)}
                      onChange={(e) => {
                        if (e.target.checked) setCategoryIds((prev) => [...prev, c.id]);
                        else setCategoryIds((prev) => prev.filter((id) => id !== c.id));
                      }}
                      className="rounded border-input h-4 w-4"
                    />
                    <span className="text-sm text-foreground">{c.name}</span>
                  </label>
                ))}
              </div>
              {(!categories || categories.length === 0) && <p className="text-sm text-amber-600 mt-2">No hay categorías. Crea al menos una en Categorías.</p>}
            </section>

            {/* Familias con las que se puede mezclar */}
            <section className="rounded-xl border bg-card p-6 shadow-sm">
              <h2 className="text-sm font-medium text-foreground mb-1">Familias con las que se puede mezclar</h2>
              <p className="text-xs text-muted-foreground mb-4">Productos de estas familias se pueden combinar en un mismo contenedor (cotización mixta).</p>
              <div className="flex flex-col sm:flex-row sm:items-end gap-4">
                <div className="w-full sm:w-[220px] space-y-2">
                  <Label className="text-xs text-muted-foreground">Filtrar por origen</Label>
                  <Select value={mixOriginFilterId || 'all'} onValueChange={(v) => setMixOriginFilterId(v === 'all' ? '' : v)}>
                    <SelectTrigger className="h-10">
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos los orígenes</SelectItem>
                      {(origins ?? []).map((o) => (
                        <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1 min-w-0 space-y-2">
                  <Label className="text-xs text-muted-foreground">Seleccionar familias</Label>
                  <MultiSelect
                    options={mixOptionCandidates.map((f) => ({ value: f.id, label: f.family_name }))}
                    value={mixOptionFamilyIds}
                    onChange={setMixOptionFamilyIds}
                    placeholder="Buscar y seleccionar familias..."
                    excludeValues={[]}
                    disabled={saveMutation.isPending}
                    searchPlaceholder="Buscar por nombre..."
                    triggerClassName="h-10 w-full justify-between min-w-0"
                  />
                  {mixOptionFamilyIds.length > 0 && (
                    <p className="text-xs text-muted-foreground">{mixOptionFamilyIds.length} familia{mixOptionFamilyIds.length !== 1 ? 's' : ''} seleccionada{mixOptionFamilyIds.length !== 1 ? 's' : ''}</p>
                  )}
                </div>
              </div>
            </section>

            {/* Imágenes */}
            <section className="rounded-xl border bg-card p-6 shadow-sm">
              <h2 className="text-sm font-medium text-foreground mb-4">Imágenes</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="min-w-0 space-y-3">
                  <Label className="text-foreground">Portada (showroom)</Label>
                  <div className="flex flex-col gap-2">
                    <div className="min-w-0">
                      <ImageUploadField label="" value={showroom_image} onChange={setShowroom_image} folder="families" />
                    </div>
                    {editing?.id && (
                      <Button type="button" variant="outline" size="sm" onClick={openAiGenerate} disabled={aiLoadingPrompt} className="w-fit">
                        <Sparkles className="h-4 w-4 mr-2" />
                        Generar imagen con IA
                      </Button>
                    )}
                    <p className="text-xs text-muted-foreground">El thumbnail se genera automáticamente desde la portada.</p>
                  </div>
                </div>
                <div className="min-w-0 space-y-3">
                  <Label className="text-foreground">Galería (imágenes secundarias)</Label>
                  <div className="min-w-0">
                    <SecondaryImagesList value={secondary_images} onChange={setSecondary_images} folder="families" />
                  </div>
                </div>
              </div>
            </section>

            {/* Marketing */}
            <section className="rounded-xl border bg-card p-6 shadow-sm">
              <h2 className="text-sm font-medium text-foreground mb-4">Descripción de marketing</h2>
              <Input value={marketing_description} onChange={(e) => setMarketing_description(e.target.value)} placeholder="Texto opcional para promoción o SEO" className="h-10 min-w-0" />
            </section>

            {/* Acciones */}
            <div className="flex flex-wrap items-center gap-3 pt-2 border-t">
              <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending || !family_name || !slug || categoryIds.length === 0}>
                {saveMutation.isPending ? 'Guardando…' : 'Guardar'}
              </Button>
              <Button type="button" variant="outline" onClick={() => { setOpen(false); setEditing(null); resetForm(); }}>
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-bold mb-2">Product families</h1>
      <div className="flex flex-wrap gap-2 mb-4">
        <Button onClick={() => { resetForm(); setEditing(null); setMixOriginFilterId(''); setStatus_id(statuses?.[0]?.id ?? ''); setOpen(true); }}>
          Add family
        </Button>
        <Button type="button" variant="outline" onClick={handleBatchGenerate} disabled={batchGenerating}>
          <Sparkles className="h-4 w-4 mr-2" />
          {batchGenerating ? 'Iniciando...' : 'Generar todas las imágenes faltantes en segundo plano'}
        </Button>
      </div>

      {/* Filtros: origen/proveedor/marca derivados de productos de la familia; búsqueda por nombre. */}
      <div className="flex flex-wrap items-center gap-3 mb-4 p-3 rounded-lg border bg-muted/40">
        <div className="relative flex-1 min-w-[180px] max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por nombre..."
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
            className="pl-8"
          />
        </div>
        <Select value={filterOriginId || 'all'} onValueChange={(v) => setFilterOriginId(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Origen" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los orígenes</SelectItem>
            {(origins ?? []).map((o) => (
              <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterSupplierId || 'all'} onValueChange={(v) => setFilterSupplierId(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Proveedor" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los proveedores</SelectItem>
            {(suppliers ?? []).map((s) => (
              <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterBrandId || 'all'} onValueChange={(v) => setFilterBrandId(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Marca" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las marcas</SelectItem>
            {(brands ?? []).map((b) => (
              <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {(searchName || filterOriginId || filterSupplierId || filterBrandId) && (
          <Button type="button" variant="ghost" size="sm" onClick={() => { setSearchName(''); setFilterOriginId(''); setFilterSupplierId(''); setFilterBrandId(''); }}>
            Limpiar filtros
          </Button>
        )}
      </div>

      {/* AI catalog image: prompt review and generate */}
      <Dialog open={aiDialogOpen} onOpenChange={setAiDialogOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>Generar imagen con IA</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-muted-foreground">Instrucción que se enviará al modelo. Puedes editarla antes de generar.</p>
            {aiLoadingPrompt ? (
              <p className="text-sm">Cargando prompt...</p>
            ) : (
              <>
                <Label>Instrucción que se enviará</Label>
                <Textarea
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  rows={6}
                  className="font-mono text-sm"
                />
                <div className="flex flex-wrap gap-2">
                  <Button onClick={confirmAiGenerate} disabled={aiGenerating || !aiPrompt.trim()}>
                    {aiGenerating ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" aria-hidden />
                        Generando…
                      </>
                    ) : (
                      'Generar'
                    )}
                  </Button>
                  <Button variant="outline" onClick={confirmAiGenerateBackground} disabled={aiGenerating || !aiPrompt.trim()}>
                    Generar en segundo plano
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {isLoading ? (
        <p className="text-secondary">Loading...</p>
      ) : (
        <>
          {(searchName || filterOriginId || filterSupplierId || filterBrandId) && (
            <p className="text-sm text-muted-foreground mb-2">
              Mostrando {filteredFamilies.length} de {(families ?? []).length} familias
            </p>
          )}
          <Table className="mt-2">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[72px]">Thumbnail</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Slug</TableHead>
              <TableHead className="w-[120px]">Generar imagen</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredFamilies.map((f) => (
              <TableRow key={f.id}>
                <TableCell className="w-[72px]">
                  {f.showroom_image ? (
                    <div className="h-12 w-14 rounded border border-zinc-200 bg-white flex items-center justify-center overflow-hidden shrink-0">
                      <StoreThumbnailImg
                        coverUrl={f.showroom_image}
                        size={{ width: 56, height: 48 }}
                        className="max-h-full max-w-full w-auto h-auto object-contain"
                      />
                    </div>
                  ) : (
                    <span className="text-zinc-400 text-xs">—</span>
                  )}
                </TableCell>
                <TableCell>{f.family_name}</TableCell>
                <TableCell>{f.slug}</TableCell>
                <TableCell>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleGenerateOneFamily(f)}
                    disabled={generatingOneIds.has(f.id)}
                    className="gap-1 shrink-0 min-w-[110px]"
                    title={f.showroom_image ? 'Regenerar imagen con IA' : 'Generar imagen con IA'}
                  >
                    {generatingOneIds.has(f.id) ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                    ) : (
                      <Sparkles className="h-3.5 w-3.5" />
                    )}
                    {generatingOneIds.has(f.id) ? 'Generando imagen…' : 'Generar imagen'}
                  </Button>
                </TableCell>
                <TableCell>
                  <Button variant="ghost" size="sm" onClick={() => openEdit(f)}>Edit</Button>
                  <Button variant="ghost" size="sm" onClick={() => deleteMutation.mutate(f.id)}>Delete</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </>
      )}
    </div>
  );
}
